import { deepmerge } from "@fastify/deepmerge";
import { Task } from "@lit/task";
import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { popover } from "../../../../directives/popover";
import { confirm } from "../../../../elements/alert-dialog";
import { Checkbox } from "../../../../elements/checkbox";
import { app } from "../../../../init";
import { sanitizeTaskContent } from "../../../../lib/sanitize";
import { debounce } from "../../../../lib/util";
import { shared } from "../../../../styles";
import type { TaskEditor } from "../task-editor/task-editor";
import "./base-tile";
import type { DashboardTile } from "./base-tile";

const mergeDashboardSettings = deepmerge();

const DEFAULT_TASK_CONTENT =
    '<ul data-type="taskList"><li data-type="taskItem" data-checked="false">Neue Aufgabe</li></ul>';

const SAVE_DELAY_MS = 1000;

@customElement("ptc-dashboard-tile-tasks")
export class DashboardTileTasks extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.tasks?.size || { inline: 2, block: 2 };

    @query("ptc-task-editor")
    private _taskEditor?: TaskEditor;

    private _formRef: Ref<HTMLFormElement> = createRef();
    private _tileRef: Ref<DashboardTile> = createRef();

    @query("ptc-checkbox-button[name='removeSubtasks']")
    private _removeSubtasksButton?: Checkbox;

    @state()
    private _isSaving = false;

    private _typedContent?: string;

    private _isConfirmingClear = false;

    get hasPendingSave() {
        return (
            this._isConfirmingClear ||
            this._isSaving ||
            (this._typedContent !== undefined && this._typedContent !== this._lastSavedContent)
        );
    }

    private _debouncedSave = debounce(async (content: string) => {
        if (content === this._lastSavedContent) {
            return;
        }
        this._isSaving = true;
        try {
            await app.api.saveNote({ content, type: "taskList" });
            this._lastSavedContent = content;
        } catch (error) {
            // @TODO: show error message to user
            console.error("Failed to save tasks:", error);
        } finally {
            this._isSaving = false;
        }
    }, SAVE_DELAY_MS);

    private _lastSavedContent?: string;

    private _tasksTask = new Task(this, {
        task: async () => {
            if (app.account?.admin && !app.profile?.id) {
                return "admin_without_profile" as const;
            }
            const note = await app.api.getNote("taskList");
            // Only sanitize content from API, not our hardcoded default
            const content = note?.content ? sanitizeTaskContent(note.content) : DEFAULT_TASK_CONTENT;
            this._lastSavedContent = content;
            return { content };
        },
        args: () => [],
    });

    connectedCallback() {
        super.connectedCallback();
        // tiptap (~564KB, vendor-editor) loads on demand when a tasks tile mounts,
        // keeping it out of the eager bundle on roster and other routes.
        void import("../task-editor/task-editor");
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    private _handleContentChange(event: CustomEvent) {
        this._scheduleSave(event.detail.content);
    }

    private _scheduleSave(content: string) {
        this._typedContent = content;
        this._debouncedSave(content);
    }

    private async _handleClearCompleted() {
        if (!this._removeSubtasksButton) {
            return;
        }

        const removeSubtasks = this._removeSubtasksButton.checked;

        if (this._tileRef.value) {
            this._tileRef.value.settingsOpen = false;
        }

        if (this._taskEditor) {
            const message = `Möchten Sie wirklich alle erledigten ${removeSubtasks ? "Aufgaben" : "Hauptaufgaben"} entfernen? Diese Aktion kann nicht rückgängig gemacht werden.`;

            this._isConfirmingClear = true;
            let confirmed: boolean;
            try {
                confirmed = await confirm(message, "Entfernen", "Abbrechen", { type: "destructive" });
            } finally {
                this._isConfirmingClear = false;
            }

            if (confirmed) {
                this._taskEditor.clearCheckedTasks(removeSubtasks);

                this._scheduleSave(this._taskEditor.content);

                this.requestUpdate();
            } else {
                if (this._tileRef.value) {
                    this._tileRef.value.settingsOpen = true;
                }
            }
        }
    }

    private _renderInfoIcon(description: string): TemplateResult {
        return html`<ptc-ui-icon
            name="icon.status.info"
            ${popover(html` <div class="small" style="max-width: 20em;">${description}</div> `, {
                trigger: "hover",
                class: "non-interactive",
                preferAlignment: ["right"],
                style: "z-index: 101;",
            })}
        ></ptc-ui-icon>`;
    }

    private _saveSettings(ev: SubmitEvent | InputEvent) {
        ev.preventDefault();

        if (!this._removeSubtasksButton) {
            return;
        }

        const removeSubtasks = this._removeSubtasksButton.checked;

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    tasks: {
                        settings: { removeSubtasks },
                    },
                },
            }),
        }).catch((e) => {
            console.error(e);
        });
    }

    private _renderSettings() {
        // @TODO: remove `!`, but for now ok, because tasks settings are always set
        const settings = app.settings.dashboard.tiles.tasks.settings!;

        const removeSubtasksLabel = html`<ptc-ui-icon name="icon.object.subtask"></ptc-ui-icon> Unteraufgaben
            mitentfernen
            ${this._renderInfoIcon(
                "Wenn aktiviert, werden erledigte Unteraufgaben entfernt – auch dann, wenn ihre Hauptaufgabe noch offen ist."
            )}`;

        return html`
            <form slot="settings" ${ref(this._formRef)} @submit=${this._saveSettings} id="tile.tasks">
                <fieldset>
                    <legend>Aufgaben entfernen</legend>

                    <div class="field">
                        <label class="input-container">
                            <ptc-checkbox-button
                                name="removeSubtasks"
                                .label=${removeSubtasksLabel}
                                .buttonClass=${"ghost"}
                                .checked=${settings.removeSubtasks}
                            ></ptc-checkbox-button>
                        </label>
                    </div>

                    <div class="field">
                        <label class="input-container"> </label>
                    </div>
                </fieldset>

                <!-- workaround for some browsers needing a explicit submit element -->
                <input type="submit" style="display: none;" />
            </form>
            <footer slot="settings-footer">
                <button class="primary" @click=${this._handleClearCompleted} part="clear-button">
                    Erledigte Aufgaben entfernen
                </button>
            </footer>
        `;
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: contents;
                position: relative;
            }

            ptc-dashboard-tile {
                --tile-content-padding: 0;
            }

            ptc-task-editor {
                border: solid 1px transparent;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-bottom-left-radius: var(--border-radius);
                border-bottom-right-radius: var(--border-radius);
                height: 100%;
                overflow: hidden;
            }

            ptc-task-editor:focus {
                border-color: var(--color-primary);
            }

            .save-indicator {
                display: flex;
                align-items: center;
                font-size: 1rem;
                gap: 0.25rem;
                color: var(--color-primary);
            }

            .clear-button {
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1rem;
                padding: 0.25rem;
                cursor: pointer;
                color: var(--shade-4);
                transition: color 0.2s;
                background: none;
                border: none;
            }

            .clear-button:hover {
                color: var(--color-primary);
            }

            .clear-button:active {
                transform: scale(0.95);
            }

            [part="spinner"] {
                font-size: 0.75rem;
            }

            [part="spinner"],
            [part="icon"] {
                width: 1lh;
                height: 1lh;
                line-height: inherit;

                transition: opacity 0.2s ease-in-out;

                @starting-style {
                    opacity: 0;
                }
            }

            [part="clear-button"] {
                border-radius: 0.5em;
                border: solid 1px var(--color-highlight, var(--shade-2));
            }

            /* Settings form styles */
            form {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            fieldset {
                border: none;
                padding: 0;
                margin: 0;
            }

            legend {
                font-weight: 600;
                margin-bottom: 0.5rem;
            }

            .field {
                margin-bottom: 0.75rem;
            }

            .input-container {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }

            .helper-text {
                font-size: 0.9em;
                color: var(--shade-4);
                margin: 0.25rem 0 0 1.5rem;
            }

            input[type="checkbox"] {
                cursor: pointer;
            }

            input[type="checkbox"]:disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                ${ref(this._tileRef)}
                style="--tile-icon-color: var(--red)"
                label="Aufgaben"
                icon="icon.object.task"
                .size=${this.size}
                .allowResize=${false}
                .settingsForm=${this._formRef.value}
                tileType="tasks"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._renderSettings()}
                ${this._isSaving
                    ? html`<span slot="header" class="save-indicator">
                          <ptc-ui-icon name="icon.action.save" part="icon"></ptc-ui-icon>
                          <ptc-ui-spinner part="spinner" active></ptc-ui-spinner>
                      </span>`
                    : ""}
                ${this._tasksTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-ui-spinner
                            class="double-padded place-self-center"
                            active
                            style="font-size: 1rem"
                        ></ptc-ui-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">Aufgaben konnten nicht geladen werden!</div>`,

                    complete: (note) => {
                        if (note === "admin_without_profile") {
                            return html`<div class="double-padded subtle text-centering">
                                Als Administrator ohne Mitarbeiterprofil in diesem Unternehmen kannst du hier keine
                                Aufgaben erstellen oder bearbeiten.
                            </div>`;
                        }
                        return html`<ptc-task-editor
                            .content=${note.content}
                            @content-changed=${this._handleContentChange}
                        ></ptc-task-editor>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-tasks": DashboardTileTasks;
    }
}
